import { navigate } from '@reach/router';
import { FormikHelpers, isNaN } from 'formik';
import React, { useContext } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { PageContainer } from '../../../layout/Layout';
import { Alert } from '../../../shared/Alert';
import { Header1 } from '../../../shared/Headers';
import { HandoverPackSectionContext } from '../documents-section/HandoverPackSectionContext';
import { handoverPackSectionsRoot } from '../documents-section/documentRoutes';
import { CreateOrUpdateHandoverPackSectionForm } from './CreateOrUpdateHandoverPackSectionForm';
import { HandoverPackSectionFormModel, mapHandoverPackSectionToFormModel } from './handoverPackSectionFormModel';
import {
  mapHandoverPackSectionFormModelToUpdateCommand,
  UpdateHandoverPackSectionDto,
} from './updateHandoverPackSectionDto';

type Props = RouteComponentProps<{ handoverPackSectionId: string }>;

export const UpdateHandoverPackSection = ({ handoverPackSectionId }: Props) => {
  const updateHandoverPackSectionEndpoint = 'handover-pack/section/update';

  const { error, sendRequest } = useApiRequest<undefined, UpdateHandoverPackSectionDto>({
    endpointUrl: updateHandoverPackSectionEndpoint,
    httpMethod: 'post',
  });

  const { refreshContext, handoverPackSections } = useContext(HandoverPackSectionContext);

  if (handoverPackSectionId === undefined || isNaN(handoverPackSectionId)) {
    return (
      <Alert
        type="error"
        title="Failed to read section ID"
        body="Failed to read section ID from URL, please double check URL before trying again"
      />
    );
  }

  const handoverPackSection = handoverPackSections.find(
    section => section.handoverPackSectionId === Number(handoverPackSectionId),
  );

  if (handoverPackSection == undefined) {
    return (
      <Alert
        type="error"
        title="Handover Pack Section not found"
        body="Handover pack section with specified ID was not found, please double check URL before trying again"
      />
    );
  }

  const onSubmit = (
    formModel: HandoverPackSectionFormModel,
    formikHelpers: FormikHelpers<HandoverPackSectionFormModel>,
  ) => {
    void sendRequest({
      requestBody: mapHandoverPackSectionFormModelToUpdateCommand(handoverPackSection.handoverPackSectionId, formModel),
      onSuccess: async () => {
        formikHelpers.setSubmitting(false);
        refreshContext();
        await navigate(handoverPackSectionsRoot);
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
        formikHelpers.setStatus('Failed');
      },
    });
  };

  return (
    <PageContainer>
      <Header1>
        Update {handoverPackSection.sectionSequence} — {handoverPackSection.sectionName}
      </Header1>
      <CreateOrUpdateHandoverPackSectionForm
        initialValues={mapHandoverPackSectionToFormModel(handoverPackSection)}
        onSubmit={onSubmit}
        error={error}
      />
    </PageContainer>
  );
};
